import { Model, View, data, route } from '@lernetz/ts-lib';
import template from './CookieBanner.html';
import { addContentItemAndTextStyleVariants } from 'paragraph/Paragraph';
import { createFocusTrap } from 'focus-trap';
import type { FocusTrap } from 'focus-trap';

export class CookieBanner extends Model {

    // banner:
    public show: boolean;

    // settings:
    public showSettings: boolean;
    public analytics: boolean;
    public marketing: boolean;

    public constructor() {
        super({
            analytics: true,
            marketing: true,
            show: false,
            showSettings: false,
        });

        this.reset();
    }

    public reset() {
        const consentSettings = data('consentSettings');

        // banner:
        const consentSettingsPresentAndComplete = typeof consentSettings !== "undefined" && typeof consentSettings.consentAnalytics === "boolean" && typeof consentSettings.consentMarketing === "boolean";
        this.show = !consentSettingsPresentAndComplete;

        // settings:
        this.showSettings = false;
        this.analytics = consentSettings?.consentAnalytics ?? true;
        this.marketing = consentSettings.consentMarketing ?? true;
    }
}

export class CookieBannerView extends View<CookieBanner> {

    private focusTrap: FocusTrap;
    private settingsFocusTrap: FocusTrap;
    private settingsTriggerHookedUp: boolean;

    public constructor() {
        super({ template });
    }

    public closeBanner() {
        // save the settings: analytics and marketing cookies are enabled by default
        window.location.href = route('updateConsentSettings' ).url() + '?analytics=1&marketing=1&url=' + encodeURIComponent(window.location.href);
    }

    public cancelSettings() {
        this.data.reset();
    }

    public updateSettings() {
        // save the settings changed by the user
        const analytics = this.data.analytics ? '1' : '0';
        const marketing = this.data.marketing ? '1' : '0';
        const url = encodeURIComponent(window.location.href);
        window.location.href = route('updateConsentSettings' ).url() + `?analytics=${analytics}&marketing=${marketing}&url=${url}`;
    }

    public init() {
        addContentItemAndTextStyleVariants(this.node);

        this.data.propertyChange.filter(p => p.name === 'show').subscribe(() => this.onShowChanged());
        this.onShowChanged();

        this.data.propertyChange.filter(p => p.name === 'showSettings').subscribe(() => this.onShowSettingsChanged());
        this.onShowSettingsChanged();
    }

    private onShowChanged() {
        if (this.data.show) {
            // prevent the user from tabbing outside the cookie banner:
            const bannerNode = this.node.querySelector<HTMLElement>('[js-banner]');
            const bannerInitialFocusNode = bannerNode.querySelector<HTMLElement>('[js-initial-focus]');
            this.focusTrap = createFocusTrap(<HTMLElement> this.node, { initialFocus: bannerInitialFocusNode }).activate();

            const settingsTriggerNode = this.node.querySelector<HTMLElement>('[js-settings-trigger]');
            if (settingsTriggerNode) {
                settingsTriggerNode.setAttribute('tabindex', '0');
                settingsTriggerNode.addEventListener('click', e => {
                    this.data.showSettings = true;
                });
                settingsTriggerNode.addEventListener('keydown', e => {
                    if (e.code !== 'Enter') return;
                    this.data.showSettings = true;
                });
            }
        }
        else {
            this.focusTrap?.deactivate();
            this.focusTrap = null;
        }
    }

    private onShowSettingsChanged() {
        if (this.data.showSettings) {
            // prevent the user from tabbing outside the cookie settings dialog:
            const settingsNode = this.node.querySelector<HTMLElement>('[js-settings]');
            const settingsInitialFocusNode = settingsNode.querySelector<HTMLElement>('[js-initial-focus]');
            this.settingsFocusTrap = createFocusTrap(settingsNode, { initialFocus: settingsInitialFocusNode }).activate();
        }
        else {
            this.settingsFocusTrap?.deactivate();
            this.settingsFocusTrap = null;
        }
    }
}
